import React, { useState, useEffect } from 'react';
import { Package, Plus, Pencil, Trash2, X, Check, Clock, IndianRupee, Tag, MapPin, ArrowRight } from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';
import Card, { CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import CarLoader from '../components/ui/CarLoader';
import ConfirmModal from '../components/ui/ConfirmModal';
import Toast from '../components/ui/Toast';
import { formatCurrency } from '../lib/utils';
import { packageAPI } from '../services/api';
import { useVehicleTypes } from '../hooks/useVehicleTypes';
import { useAuth } from '../context/AuthContext';

const PACKAGE_TYPES = ['hourly', 'daily', 'weekly', 'monthly', 'tour', 'custom'];
const DURATION_UNITS = ['hours', 'days', 'weeks', 'months'];

const emptyForm = {
  name: '',
  packageType: 'daily',
  vehicleType: 'any',
  startPoint: '',
  placesList: [''],
  description: '',
  duration: { value: 1, unit: 'days' },
  basePrice: '',
  discountPercentage: 0,
  includedKm: 0,
  extraKmCharge: 0,
  features: '',
  isActive: true
};

const Packages = () => {
  const { vehicleTypes: vehicleTypeOptions, getLabel: getVehicleTypeLabel } = useVehicleTypes({ includeAny: true });
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin'; // admin = read-only monitor (no add/edit/delete), paginated
  const [pagination, setPagination] = useState({ page: 1, limit: 12, total: 0, pages: 0 });
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', type: 'danger', confirmText: 'Confirm', onConfirm: null });
  const [toast, setToast] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  const fetchPackages = async (page = 1) => {
    try {
      setLoading(true);
      // Admin gets paginated results (latest 12); owners get their full list.
      const res = await packageAPI.getMy(isAdmin ? { page, limit: 12 } : undefined);
      setPackages(res.data.packages || []);
      if (res.data.pagination) setPagination(res.data.pagination);
    } catch (err) {
      console.error('Error fetching packages:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages(1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setError('');
    setShowForm(true);
  };

  const openEdit = (pkg) => {
    setForm({
      name: pkg.name || '',
      packageType: pkg.packageType || 'daily',
      vehicleType: pkg.vehicleType || 'any',
      startPoint: pkg.startPoint || '',
      placesList: (pkg.places && pkg.places.length) ? [...pkg.places] : [''],
      description: pkg.description || '',
      duration: { value: pkg.duration?.value || 1, unit: pkg.duration?.unit || 'days' },
      basePrice: pkg.basePrice ?? '',
      discountPercentage: pkg.discountPercentage || 0,
      includedKm: pkg.includedKm || 0,
      extraKmCharge: pkg.extraKmCharge || 0,
      features: (pkg.features || []).join(', '),
      isActive: pkg.isActive !== false
    });
    setEditingId(pkg._id);
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || form.basePrice === '' || Number(form.basePrice) < 0) {
      setError('Please enter a package name and a valid base price.');
      return;
    }
    setSaving(true);
    setError('');
    const payload = {
      name: form.name.trim(),
      packageType: form.packageType,
      vehicleType: form.vehicleType,
      startPoint: form.startPoint.trim(),
      places: form.placesList.map(p => p.trim()).filter(Boolean),
      description: form.description.trim(),
      duration: { value: Number(form.duration.value) || 1, unit: form.duration.unit },
      basePrice: Number(form.basePrice),
      discountPercentage: Number(form.discountPercentage) || 0,
      includedKm: Number(form.includedKm) || 0,
      extraKmCharge: Number(form.extraKmCharge) || 0,
      features: form.features.split(',').map(f => f.trim()).filter(Boolean),
      isActive: form.isActive
    };
    try {
      if (editingId) {
        await packageAPI.update(editingId, payload);
      } else {
        await packageAPI.create(payload);
      }
      setShowForm(false);
      fetchPackages();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save package.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setConfirm({
      isOpen: true,
      title: 'Delete Package',
      message: 'Delete this package? This cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await packageAPI.delete(id);
          setPackages(prev => prev.filter(p => p._id !== id));
        } catch (err) {
          setToast({ isOpen: true, title: 'Error', message: err.response?.data?.message || 'Failed to delete package.', type: 'error' });
        }
      }
    });
  };

  const finalPrice = (pkg) => pkg.basePrice - (pkg.basePrice * (pkg.discountPercentage || 0) / 100);

  // Dynamic destination (To) stops
  const updatePlace = (idx, value) => setForm(f => {
    const list = [...f.placesList];
    list[idx] = value;
    return { ...f, placesList: list };
  });
  const addPlace = () => setForm(f => ({ ...f, placesList: [...f.placesList, ''] }));
  const removePlace = (idx) => setForm(f => ({
    ...f,
    placesList: f.placesList.length > 1 ? f.placesList.filter((_, i) => i !== idx) : ['']
  }));

  // Show a full route string e.g. "Bangalore → Tirupati → Tirumala"
  const routeText = (pkg) => [pkg.startPoint, ...(pkg.places || [])].filter(Boolean).join('  →  ');

  return (
    <div className="space-y-4">
      <PageHeader
        title="Packages"
        description={isAdmin
          ? 'Monitoring view — all owners\' packages (read-only).'
          : 'Create rental or tour packages (with route, places, days & price) that customers see on your public page.'}
        action={isAdmin ? null : (
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" /> Add Package
          </Button>
        )}
      />

      {loading ? (
        <div className="flex items-center justify-center min-h-[40vh]">
          <CarLoader size="large" message="Loading packages..." />
        </div>
      ) : packages.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-gray-900 mb-1">No packages yet</h3>
            <p className="text-sm text-gray-500 mb-4">{isAdmin ? 'No packages found.' : 'Add your first package — it will appear on your public travels page.'}</p>
            {!isAdmin && (
              <Button onClick={openCreate}>
                <Plus className="h-4 w-4 mr-1" /> Add Package
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {packages.map(pkg => (
            <Card key={pkg._id} className="relative">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0"><Package className="h-4 w-4 text-blue-600" /></div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-gray-900 truncate">{pkg.name}</h3>
                      <span className="text-[11px] text-gray-500 capitalize">{pkg.packageType} · {getVehicleTypeLabel(pkg.vehicleType)}</span>
                    </div>
                  </div>
                  {pkg.isActive === false && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">Hidden</span>
                  )}
                </div>

                <div className="flex items-baseline gap-1.5 mb-3">
                  <span className="text-xl font-bold text-gray-900">{formatCurrency(finalPrice(pkg))}</span>
                  {pkg.discountPercentage > 0 && (
                    <>
                      <span className="text-xs text-gray-400 line-through">{formatCurrency(pkg.basePrice)}</span>
                      <span className="text-[10px] font-medium px-1 py-0.5 bg-green-100 text-green-700 rounded">{pkg.discountPercentage}% off</span>
                    </>
                  )}
                </div>

                {routeText(pkg) && (
                  <div className="flex items-start gap-1.5 text-xs text-gray-700 mb-2 bg-gray-50 rounded-lg p-2">
                    <MapPin className="h-3.5 w-3.5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="font-medium">{routeText(pkg)}</span>
                  </div>
                )}

                <div className="space-y-1 text-xs text-gray-600 mb-3">
                  <div className="flex items-center gap-1.5"><Clock className="h-3 w-3 text-gray-400" /> {pkg.duration?.value} {pkg.duration?.unit}</div>
                  {pkg.includedKm > 0 && <div className="flex items-center gap-1.5"><Tag className="h-3 w-3 text-gray-400" /> {pkg.includedKm} km included</div>}
                  {pkg.extraKmCharge > 0 && <div className="flex items-center gap-1.5"><IndianRupee className="h-3 w-3 text-gray-400" /> {formatCurrency(pkg.extraKmCharge)}/extra km</div>}
                </div>

                {pkg.description && <p className="text-xs text-gray-500 mb-3 line-clamp-2">{pkg.description}</p>}

                {pkg.features?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {pkg.features.map((f, i) => (
                      <span key={i} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{f}</span>
                    ))}
                  </div>
                )}

                {!isAdmin && (
                <div className="flex gap-2 pt-2 border-t border-gray-100">
                  <button onClick={() => openEdit(pkg)} className="flex-1 inline-flex items-center justify-center gap-1 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button onClick={() => handleDelete(pkg._id)} className="inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                )}
                {isAdmin && pkg.ownerId?.travelsInfo?.name && (
                  <div className="pt-2 border-t border-gray-100 text-[11px] text-gray-400">Agency: {pkg.ownerId.travelsInfo.name}</div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination — admin monitoring view only */}
      {isAdmin && !loading && pagination.pages > 1 && (
        <div className="flex items-center justify-between pt-2 text-sm text-gray-600">
          <span>Page {pagination.page} of {pagination.pages} · {pagination.total} total</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchPackages(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
              Prev
            </button>
            <button
              onClick={() => fetchPackages(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages}
              className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">{editingId ? 'Edit Package' : 'Add Package'}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Package Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., Weekend Getaway, City Tour 8hr/80km"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">For Vehicle</label>
                <select
                  value={form.vehicleType}
                  onChange={e => setForm(f => ({ ...f, vehicleType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                >
                  {vehicleTypeOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              {/* Route — From → To stops (optional; great for tour packages) */}
              <div className="bg-gray-50 rounded-xl p-3 space-y-2.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-blue-600" /> Route &amp; Places <span className="font-normal text-gray-400">(optional)</span>
                  </label>
                  <button type="button" onClick={addPlace} className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700">
                    <Plus className="h-3.5 w-3.5" /> Add stop
                  </button>
                </div>
                <input
                  type="text"
                  value={form.startPoint}
                  onChange={e => setForm(f => ({ ...f, startPoint: e.target.value }))}
                  placeholder="From (e.g., Bangalore)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
                {form.placesList.map((place, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <ArrowRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      value={place}
                      onChange={e => updatePlace(idx, e.target.value)}
                      placeholder={`Destination ${idx + 1} (e.g., ${idx === 0 ? 'Tirupati' : 'Tirumala'})`}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    <button type="button" onClick={() => removePlace(idx)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-md flex-shrink-0">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={form.packageType}
                    onChange={e => setForm(f => ({ ...f, packageType: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm capitalize focus:ring-2 focus:ring-blue-500"
                  >
                    {PACKAGE_TYPES.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Base Price (₹) *</label>
                  <input
                    type="number" min="0"
                    value={form.basePrice}
                    onChange={e => setForm(f => ({ ...f, basePrice: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Duration</label>
                  <input
                    type="number" min="1"
                    value={form.duration.value}
                    onChange={e => setForm(f => ({ ...f, duration: { ...f.duration, value: e.target.value } }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Unit</label>
                  <select
                    value={form.duration.unit}
                    onChange={e => setForm(f => ({ ...f, duration: { ...f.duration, unit: e.target.value } }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm capitalize focus:ring-2 focus:ring-blue-500"
                  >
                    {DURATION_UNITS.map(u => <option key={u} value={u} className="capitalize">{u}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Discount %</label>
                  <input type="number" min="0" max="100" value={form.discountPercentage}
                    onChange={e => setForm(f => ({ ...f, discountPercentage: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Included km</label>
                  <input type="number" min="0" value={form.includedKm}
                    onChange={e => setForm(f => ({ ...f, includedKm: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Extra km ₹</label>
                  <input type="number" min="0" value={form.extraKmCharge}
                    onChange={e => setForm(f => ({ ...f, extraKmCharge: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Features (comma separated)</label>
                <input
                  type="text"
                  value={form.features}
                  onChange={e => setForm(f => ({ ...f, features: e.target.value }))}
                  placeholder="AC, Driver included, Fuel included"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description / Itinerary <span className="font-normal text-gray-400">(optional)</span></label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="e.g., Darshan at Tirumala, visit to Kanipakam temple, hotel pickup & drop, all tolls included…"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.isActive}
                  onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300" />
                <span className="text-sm text-gray-700">Show on public page</span>
              </label>

              {error && <p className="text-xs text-red-600 bg-red-50 p-2 rounded-lg">{error}</p>}
            </div>

            <div className="flex gap-3 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setShowForm(false)} className="flex-1 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 rounded-xl">
                <Check className="h-4 w-4" /> {saving ? 'Saving...' : (editingId ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal isOpen={confirm.isOpen} onClose={() => setConfirm(p => ({ ...p, isOpen: false }))} onConfirm={confirm.onConfirm} title={confirm.title} message={confirm.message} type={confirm.type} confirmText={confirm.confirmText} />
      <Toast isOpen={toast.isOpen} onClose={() => setToast(p => ({ ...p, isOpen: false }))} title={toast.title} message={toast.message} type={toast.type} />
    </div>
  );
};

export default Packages;
